// Homepage integrations section: the three ways LLM traffic reaches LangWatch,
// mirroring the hero chips (own agents, coding agents, gateway). Logos reuse
// the CustomerLogos ink-silhouette treatment so the strip reads as one system
// on the paper background.

type Lane = {
  number: string
  title: string
  body: string
  command?: string
  logos: { src: string; alt: string }[]
  link: { label: string; href: string }
}

const LANES: Lane[] = [
  {
    number: '01',
    title: 'The agents you build',
    body:
      'Instrument with the Python or TypeScript SDK, or plain OpenTelemetry. LangChain, LangGraph, CrewAI, Mastra, Pydantic AI, the Vercel AI SDK and every other framework send traces without a rewrite.',
    logos: [
      { src: '/vendor/opentelemetry.svg', alt: 'OpenTelemetry' },
      { src: '/vendor/python.svg', alt: 'Python' },
      { src: '/vendor/nodejs.svg', alt: 'Node.js' },
      { src: '/vendor/langchain.svg', alt: 'LangChain' },
      { src: '/vendor/langgraph.svg', alt: 'LangGraph' },
      { src: '/vendor/crewai.svg', alt: 'CrewAI' },
      { src: '/vendor/mastra.svg', alt: 'Mastra' },
      { src: '/vendor/pydantic.svg', alt: 'Pydantic AI' },
      { src: '/vendor/vercel.svg', alt: 'Vercel AI SDK' },
      { src: '/vendor/n8n.svg', alt: 'n8n' },
    ],
    link: { label: 'Browse all integrations', href: '/integrations' },
  },
  {
    number: '02',
    title: 'The coding agents your engineers run',
    body:
      'One command brings Claude Code, Codex, Copilot and opencode onto the same observability standard, with tokens, cost and cache accounted for per engineer and per repository.',
    command: 'npx langwatch claude',
    logos: [
      { src: '/vendor/claudecode-color.svg', alt: 'Claude Code' },
      { src: '/vendor/codex-color.svg', alt: 'Codex' },
      { src: '/vendor/github-copilot-icon.svg', alt: 'GitHub Copilot' },
      { src: '/vendor/opencode.svg', alt: 'opencode' },
      { src: '/vendor/cursor.svg', alt: 'Cursor' },
      { src: '/vendor/cline.svg', alt: 'Cline' },
      { src: '/vendor/windsurf.svg', alt: 'Windsurf' },
      { src: '/vendor/zed.svg', alt: 'Zed' },
    ],
    link: { label: 'Track your coding agents', href: '/claude-code-usage' },
  },
  {
    number: '03',
    title: 'Every model, through the gateway',
    body:
      'Route OpenAI, Anthropic, Google, Bedrock, Mistral and any other provider through one gateway with your own keys, budgets per person and team, and 11 microseconds of overhead.',
    logos: [
      { src: '/vendor/openai.svg', alt: 'OpenAI' },
      { src: '/vendor/claude.svg', alt: 'Anthropic Claude' },
      { src: '/vendor/gemini.svg', alt: 'Google Gemini' },
      { src: '/vendor/aws-color.svg', alt: 'Amazon Bedrock' },
      { src: '/vendor/microsoft-color.svg', alt: 'Microsoft Azure' },
      { src: '/vendor/mistral-color.svg', alt: 'Mistral' },
      { src: '/vendor/groq.svg', alt: 'Groq' },
      { src: '/vendor/deepseek.svg', alt: 'DeepSeek' },
      { src: '/vendor/vertex-color.svg', alt: 'Vertex AI' },
    ],
    link: { label: 'Put every call on a budget', href: '/gateway' },
  },
]

function LogoStrip({ logos }: { logos: Lane['logos'] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      {logos.map((l) => (
        <img
          key={l.src}
          src={l.src}
          alt={l.alt}
          className="h-5 w-auto shrink-0 object-contain opacity-60 brightness-0 transition duration-300 hover:opacity-100 hover:brightness-100"
        />
      ))}
    </div>
  )
}

export function SectionIntegrations() {
  return (
    <section id="integrations" className="relative bg-paper">
      <div className="container-rail pt-16 pb-24 md:pt-24 md:pb-28">
        <div className="mx-auto max-w-[1120px]">
          <span className="pill">Integrations</span>
          <h2 className="mt-5 max-w-[720px] text-[26px] font-semibold leading-[1.14] tracking-[-0.02em] text-ink-950 md:text-[33px]">
            Every LLM call has a way in
          </h2>
          <p className="mt-5 max-w-[640px] text-[16px] leading-[1.7] text-ink-600">
            OpenTelemetry for the agents you build, one command for the coding
            agents your engineers run, and the gateway for every model in
            between. It all lands in the same traces, evaluations and budgets.
          </p>

          <div className="mt-14 divide-y divide-ink-100 border-y border-ink-100">
            {LANES.map((lane) => (
              <div
                key={lane.number}
                className="grid gap-6 py-10 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)] md:gap-10"
              >
                <div className="text-[13px] font-medium tracking-[0.18em] text-ink-400">
                  {lane.number}
                </div>
                <div>
                  <h3 className="text-[19px] font-semibold tracking-[-0.01em] text-ink-950">
                    {lane.title}
                  </h3>
                  <p className="mt-3 max-w-[460px] text-[15px] leading-[1.7] text-ink-600">
                    {lane.body}
                  </p>
                  {lane.command ? (
                    <code className="mt-4 inline-block rounded-lg bg-ink-950 px-3 py-1.5 font-mono text-[13px] text-paper">
                      {lane.command}
                    </code>
                  ) : null}
                </div>
                <div className="flex flex-col justify-between gap-6">
                  <LogoStrip logos={lane.logos} />
                  <a
                    href={lane.link.href}
                    className="text-[14px] font-medium text-ink-900 underline decoration-ink-300 underline-offset-4 transition-colors hover:decoration-ink-900"
                  >
                    {lane.link.label}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
